import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../api/api";
import { ERROR_MESSAGES } from "../../constants/enums";
import { IPhoneExtension, IPhoneDid, IPhoneCdr, IPhoneQueue, IPhoneBook, IPhoneSimpleCdr, IPhoneCdrAiAnalysis, IClickToCallPayload, IClickToCallResponse, IPhoneIvr, IPhoneVoicemail, IPhoneMediaFile, IPhoneFeatureCode, IPhoneBookEntry, IPhoneCustomDestination, IPhoneHuntList, IPhoneCondition } from "./phone.types";

// Load the signed-in user's phone_ext for click-to-call / WebRTC gating.
// Backed by the existing `/user/get-users?user_fact_id=` endpoint; no new
// endpoint is required because the extension already lives on the user
// profile and is validated by `/api/v1/webrtc/config/:number`.
export const fetchMyPhoneExt = createAsyncThunk<
  { phone_ext: string | null },
  number,
  { rejectValue: { message: string; status?: number } }
>("phone/fetchMyPhoneExt", async (userFactId, { rejectWithValue }) => {
  try {
    const res = await api.get(`/user/get-users?user_fact_id=${userFactId}`);
    const rows = res.data?.data || [];
    const ext = rows?.[0]?.phone_ext;
    return { phone_ext: ext ? String(ext) : null };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const fetchPhoneExtensions = createAsyncThunk<
  { data: IPhoneExtension[] },
  void,
  { rejectValue: { message: string; status?: number } }
>("phone/fetchPhoneExtensions", async (_, { rejectWithValue }) => {
  try {
    const res = await api.get("/mirtapbx/extensions");
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const fetchPhoneExtensionById = createAsyncThunk<
  { data: IPhoneExtension },
  number,
  { rejectValue: { message: string; status?: number } }
>("phone/fetchPhoneExtensionById", async (id, { rejectWithValue }) => {
  try {
    const res = await api.get(`/mirtapbx/extensions/${id}`);
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const fetchPhoneExtensionByNumber = createAsyncThunk<
  { data: IPhoneExtension },
  string,
  { rejectValue: { message: string; status?: number } }
>("phone/fetchPhoneExtensionByNumber", async (number, { rejectWithValue }) => {
  try {
    const res = await api.get(`/mirtapbx/extensions/number/${number}`);
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const createPhoneExtension = createAsyncThunk<
  { data: IPhoneExtension },
  Partial<IPhoneExtension>,
  { rejectValue: { message: string; status?: number } }
>("phone/createPhoneExtension", async (payload, { rejectWithValue }) => {
  try {
    const res = await api.post("/mirtapbx/extensions", payload);
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const updatePhoneExtension = createAsyncThunk<
  { data: IPhoneExtension },
  { id: number; payload: Partial<IPhoneExtension> },
  { rejectValue: { message: string; status?: number } }
>("phone/updatePhoneExtension", async ({ id, payload }, { rejectWithValue }) => {
  try {
    const res = await api.patch(`/mirtapbx/extensions/${id}`, payload);
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const deletePhoneExtension = createAsyncThunk<
  { data: string },
  number,
  { rejectValue: { message: string; status?: number } }
>("phone/deletePhoneExtension", async (id, { rejectWithValue }) => {
  try {
    const res = await api.delete(`/mirtapbx/extensions/${id}`);
    return { data: res.data.message || "Extension deleted successfully" };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const fetchPhoneDids = createAsyncThunk<
  { data: IPhoneDid[] },
  void,
  { rejectValue: { message: string; status?: number } }
>("phone/fetchPhoneDids", async (_, { rejectWithValue }) => {
  try {
    const res = await api.get("/mirtapbx/dids");
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const createPhoneDid = createAsyncThunk<
  { data: IPhoneDid },
  Partial<IPhoneDid>,
  { rejectValue: { message: string; status?: number } }
>("phone/createPhoneDid", async (payload, { rejectWithValue }) => {
  try {
    const res = await api.post("/mirtapbx/dids", payload);
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});


export const fetchPhoneCdrs = createAsyncThunk<
  { data: IPhoneCdr[] },
  { start?: string; end?: string; direction?: string; disposition?: string; phone?: string; linkedid?: string; uniqueid?: string; realsrc?: string } | void,
  { rejectValue: { message: string; status?: number } }
>("phone/fetchPhoneCdrs", async (params, { rejectWithValue }) => {
  try {
    const queryParts: string[] = [];
    // If the caller already provides a time portion (has a space), use it as-is;
    // otherwise fall back to the default day boundaries.
    if (params && params.start) {
      const startVal = params.start.includes(" ") ? params.start : `${params.start} 00:00:00`;
      queryParts.push(`start=${startVal}`);
    }
    if (params && params.end) {
      const endVal = params.end.includes(" ") ? params.end : `${params.end} 23:00:00`;
      queryParts.push(`end=${endVal}`);
    }
    if (params && params.direction) queryParts.push(`direction=${params.direction}`);
    if (params && params.disposition) queryParts.push(`disposition=${params.disposition}`);
    if (params && params.phone) queryParts.push(`phone=${params.phone}`);
    if (params && params.linkedid) queryParts.push(`linkedid=${params.linkedid}`);
    if (params && params.uniqueid) queryParts.push(`uniqueid=${params.uniqueid}`);
    if (params && params.realsrc) queryParts.push(`realsrc=${params.realsrc}`);
    const queryString = queryParts.length > 0 ? `?${queryParts.join("&")}` : "";
    const res = await api.get(`/mirtapbx/calls/cdrs${queryString}`);
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});


export const fetchPhoneQueues = createAsyncThunk<
  { data: IPhoneQueue[] },
  void,
  { rejectValue: { message: string; status?: number } }
>("phone/fetchPhoneQueues", async (_, { rejectWithValue }) => {
  try {
    const res = await api.get("/mirtapbx/queues");
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});


export const fetchPhoneBooks = createAsyncThunk<
  { data: IPhoneBook[] },
  void,
  { rejectValue: { message: string; status?: number } }
>("phone/fetchPhoneBooks", async (_, { rejectWithValue }) => {
  try {
    const res = await api.get("/mirtapbx/phonebooks");
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const createPhoneBook = createAsyncThunk<
  { data: IPhoneBook },
  Partial<IPhoneBook>,
  { rejectValue: { message: string; status?: number } }
>("phone/createPhoneBook", async (payload, { rejectWithValue }) => {
  try {
    const res = await api.post("/mirtapbx/phonebooks", payload);
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const fetchPhoneSimpleCdrs = createAsyncThunk<
  { data: IPhoneSimpleCdr[] },
  { start?: string; end?: string; direction?: string; disposition?: string; dialedFrom?: string; dialedTo?: string; whoanswered?: string; dialednum?: string; calleridnum?: string; linkedid?: string; uniqueid?: string } | void,
  { rejectValue: { message: string; status?: number } }
>("phone/fetchPhoneSimpleCdrs", async (params, { rejectWithValue }) => {
  try {
    const queryParts: string[] = [];
    // If the caller already provides a time portion (has a space), use it as-is;
    // otherwise fall back to the default day boundaries.
    if (params && params.start) {
      const startVal = params.start.includes(" ") ? params.start : `${params.start} 00:00:00`;
      queryParts.push(`start=${startVal}`);
    }
    if (params && params.end) {
      const endVal = params.end.includes(" ") ? params.end : `${params.end} 23:00:00`;
      queryParts.push(`end=${endVal}`);
    }
    if (params && params.direction) queryParts.push(`direction=${params.direction}`);
    if (params && params.disposition) queryParts.push(`disposition=${params.disposition}`);
    if (params && params.dialedFrom) {
      queryParts.push(`calleridnum=${params.dialedFrom}`);
    }
    if (params && params.dialedTo) {
      queryParts.push(`sc_dialednum=${params.dialedTo}`);
    }
    if (params && params.whoanswered) {
      queryParts.push(`whoanswered=${params.whoanswered}`);
    }
    if (params && params.dialednum) {
      queryParts.push(`dialednum=${params.dialednum}`);
    }
    if (params && params.calleridnum) {
      queryParts.push(`calleridnum=${params.calleridnum}`);
    }
    if (params && params.linkedid) queryParts.push(`linkedid=${params.linkedid}`);
    if (params && params.uniqueid) queryParts.push(`uniqueid=${params.uniqueid}`);
    const queryString = queryParts.length > 0 ? `?${queryParts.join("&")}` : "";
    const res = await api.get(`/mirtapbx/calls/simple-cdrs${queryString}`);
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});


export const fetchPhoneCdrAiAnalysis = createAsyncThunk<
  { data: IPhoneCdrAiAnalysis | null },
  string,
  { rejectValue: { message: string; status?: number } }
>("phone/fetchPhoneCdrAiAnalysis", async (uniqueid, { rejectWithValue }) => {
  try {
    const res = await api.get(`/mirtapbx/calls/cdrs/${uniqueid}/ai-analysis`);
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});


export const initiateClickToCall = createAsyncThunk<
  IClickToCallResponse,
  IClickToCallPayload,
  { rejectValue: { message: string; status?: number } }
>("phone/initiateClickToCall", async (payload, { rejectWithValue }) => {
  try {
    // Build the request body from the incoming payload. CRM identifiers
    // (customer_id, customer_policy_id, lead_id) are additive and only
    // included when present, so legacy callers that omit them send the
    // exact same body as before.
    const { customer_id, customer_policy_id, lead_id, ...rest } = payload;
    const body: IClickToCallPayload = { ...rest };
    if (customer_id !== undefined && customer_id !== null) body.customer_id = customer_id;
    if (customer_policy_id !== undefined && customer_policy_id !== null) body.customer_policy_id = customer_policy_id;
    if (lead_id !== undefined && lead_id !== null) body.lead_id = lead_id;
    const res = await api.post("/mirtapbx/calls/click-to-call", body);
    // Response may carry a call_ref used to correlate the dial's context.
    return res.data.data || res.data;
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const fetchPhoneIvrs = createAsyncThunk<
  { data: IPhoneIvr[] },
  void,
  { rejectValue: { message: string; status?: number } }
>("phone/fetchPhoneIvrs", async (_, { rejectWithValue }) => {
  try {
    const res = await api.get("/mirtapbx/ivrs");
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const createPhoneIvr = createAsyncThunk<
  { data: IPhoneIvr },
  Partial<IPhoneIvr>,
  { rejectValue: { message: string; status?: number } }
>("phone/createPhoneIvr", async (payload, { rejectWithValue }) => {
  try {
    const res = await api.post("/mirtapbx/ivrs", payload);
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const fetchPhoneVoicemails = createAsyncThunk<
  { data: IPhoneVoicemail[] },
  void,
  { rejectValue: { message: string; status?: number } }
>("phone/fetchPhoneVoicemails", async (_, { rejectWithValue }) => {
  try {
    const res = await api.get("/mirtapbx/voicemails");
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const fetchPhoneMediaFiles = createAsyncThunk<
  { data: IPhoneMediaFile[] },
  void,
  { rejectValue: { message: string; status?: number } }
>("phone/fetchPhoneMediaFiles", async (_, { rejectWithValue }) => {
  try {
    const res = await api.get("/mirtapbx/media-files");
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const fetchPhoneFeatureCodes = createAsyncThunk<
  { data: IPhoneFeatureCode[] },
  void,
  { rejectValue: { message: string; status?: number } }
>("phone/fetchPhoneFeatureCodes", async (_, { rejectWithValue }) => {
  try {
    const res = await api.get("/mirtapbx/feature-codes");
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const fetchPhoneBookEntries = createAsyncThunk<
  { data: IPhoneBookEntry[] },
  { phonebook_id?: string } | void,
  { rejectValue: { message: string; status?: number } }
>("phone/fetchPhoneBookEntries", async (params, { rejectWithValue }) => {
  try {
    const queryParams: Record<string, string> = {};
    if (params && params.phonebook_id) queryParams.phonebook_id = params.phonebook_id;
    const res = await api.get("/mirtapbx/phonebook-entries", { params: queryParams });
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const fetchPhoneCustomDestinations = createAsyncThunk<
  { data: IPhoneCustomDestination[] },
  void,
  { rejectValue: { message: string; status?: number } }
>("phone/fetchPhoneCustomDestinations", async (_, { rejectWithValue }) => {
  try {
    const res = await api.get("/mirtapbx/custom-destinations");
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const fetchPhoneHuntLists = createAsyncThunk<
  { data: IPhoneHuntList[] },
  void,
  { rejectValue: { message: string; status?: number } }
>("phone/fetchPhoneHuntLists", async (_, { rejectWithValue }) => {
  try {
    const res = await api.get("/mirtapbx/hunt-lists");
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const fetchPhoneConditions = createAsyncThunk<
  { data: IPhoneCondition[] },
  void,
  { rejectValue: { message: string; status?: number } }
>("phone/fetchPhoneConditions", async (_, { rejectWithValue }) => {
  try {
    const res = await api.get("/mirtapbx/conditions");
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const fetchVoicemailMessages = createAsyncThunk<
  { data: any[] },
  string,
  { rejectValue: { message: string; status?: number } }
>("phone/fetchVoicemailMessages", async (mailbox, { rejectWithValue }) => {
  try {
    const res = await api.get(`/mirtapbx/voicemails/mailbox/${mailbox}/messages`);
    return { data: res.data.data };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});

export const fetchVoicemailAudio = createAsyncThunk<
  { data: string },
  { mailbox: string; msgid: string },
  { rejectValue: { message: string; status?: number } }
>("phone/fetchVoicemailAudio", async ({ mailbox, msgid }, { rejectWithValue }) => {
  try {
    const encodedMsgId = encodeURIComponent(msgid);
    const res = await api.get(`/mirtapbx/voicemails/mailbox/${mailbox}/messages/${encodedMsgId}/audio`, {
      responseType: "blob",
    });
    const contentType = (res.headers?.["content-type"] as string) || "audio/wav";
    if (!contentType.startsWith("audio/") && !contentType.includes("octet-stream")) {
      const preview = await (res.data as Blob).text();
      throw new Error(preview?.slice(0, 200) || "Non-audio response");
    }
    const blob = res.data instanceof Blob
      ? new Blob([res.data], { type: contentType })
      : new Blob([res.data as ArrayBuffer], { type: contentType });
    const url = URL.createObjectURL(blob);
    return { data: url };
  } catch (err: any) {
    return rejectWithValue({
      message: err.response?.data?.message || err.message || ERROR_MESSAGES.SERVER_ERROR,
      status: err.response?.status,
    });
  }
});
